import * as THREE from 'three';

/** What the trail reads off a unit's snapshot state. */
export interface ChargeRunner { id: string; radius: number; health: { dead: boolean }; combat: { charging?: boolean; charge?: boolean } }

const STREAKS = 7, PUFFS = 64, PUFF_LIFE = .9, PUFF_SPACING = .32;
/** Metres per game second a charger must be covering for its run to streak; below it the trail settles. */
export const CHARGE_TRAIL_SPEED = 1.2;
const DUST = 0xb39d7b;

function streakMaterial() {
  return new THREE.ShaderMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, toneMapped: false,
    uniforms: { uTime: { value: 0 }, uOpacity: { value: 0 }, uColor: { value: new THREE.Color(0xfff1d8) } },
    vertexShader: `
      attribute float aSeed; varying vec2 vUv; varying float vSeed;
      void main() { vUv = uv; vSeed = aSeed; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `
      uniform float uTime; uniform float uOpacity; uniform vec3 uColor; varying vec2 vUv; varying float vSeed;
      void main() {
        float across = 1.0 - pow(abs(vUv.y * 2.0 - 1.0), 2.0);
        float tail = pow(1.0 - vUv.x, 1.6) * smoothstep(0.0, .06, vUv.x);
        float rush = .55 + .45 * sin(vUv.x * 18.0 + uTime * 46.0 + vSeed * 6.283);
        gl_FragColor = vec4(uColor, across * tail * rush * uOpacity * (.35 + vSeed * .4));
      }`,
  });
}

/** Thin crossed strips along local -Z (behind the runner), one unit long, at assorted heights and offsets around the body. */
function streakGeometry(height: number, radius: number) {
  const positions: number[] = [], uv: number[] = [], seeds: number[] = [], indices: number[] = [];
  for (let i = 0; i < STREAKS; i++) {
    const seed = ((i * .61803398875) % 1), y = height * (.2 + .7 * ((i * .41421356) % 1)), x = radius * (seed - .5) * 1.4;
    const width = .035 + seed * .04, start = radius * (.2 + seed * .3), length = .55 + seed * .45;
    for (const [dx, dy] of [[width, 0], [0, width]]) {
      const base = positions.length / 3;
      for (const [along, side] of [[0, -1], [0, 1], [1, -1], [1, 1]]) {
        positions.push(x + dx * side, y + dy * side, -(start + along * length)); uv.push(along, side < 0 ? 0 : 1); seeds.push(seed);
      }
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3)); geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.setAttribute('aSeed', new THREE.Float32BufferAttribute(seeds, 1)); geometry.setIndex(indices); return geometry;
}

/** A ring of dust puffs kicked up behind the runner, aged on the game clock so a pause freezes them. */
function dustPoints() {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(PUFFS * 3), 3));
  geometry.setAttribute('aBirth', new THREE.Float32BufferAttribute(new Float32Array(PUFFS).fill(-1e6), 1));
  geometry.setAttribute('aSeed', new THREE.Float32BufferAttribute(Array.from({ length: PUFFS }, (_, i) => (i * .61803398875) % 1), 1));
  const material = new THREE.ShaderMaterial({ transparent: true, depthWrite: false, toneMapped: false,
    uniforms: { uTime: { value: 0 }, uLife: { value: PUFF_LIFE }, uPpm: { value: 40 }, uColor: { value: new THREE.Color(DUST) } },
    vertexShader: `
      attribute float aBirth; attribute float aSeed; uniform float uTime; uniform float uLife; uniform float uPpm; varying float vAlpha;
      void main() {
        float age = (uTime - aBirth) / uLife;
        vec3 p = position + vec3(sin(aSeed * 41.0) * .25, .15 + age * (.35 + aSeed * .3), cos(aSeed * 29.0) * .25) * clamp(age, 0.0, 1.0);
        vAlpha = age < 0.0 || age > 1.0 ? 0.0 : smoothstep(0.0, .12, age) * pow(1.0 - age, 1.4) * (.35 + aSeed * .2);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        gl_PointSize = vAlpha > 0.0 ? clamp(uPpm * (.35 + age * .9) * (.8 + aSeed * .4), 2.0, 96.0) : 0.0;
      }`,
    fragmentShader: `
      uniform vec3 uColor; varying float vAlpha;
      void main() {
        float r = length(gl_PointCoord - .5) * 2.0;
        gl_FragColor = vec4(uColor, pow(max(0.0, 1.0 - r), 1.8) * vAlpha);
      }`,
  });
  const points = new THREE.Points(geometry, material); points.frustumCulled = false; points.name = 'charge-dust'; return points;
}

interface Trail {
  streaks: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>; dust: THREE.Points<THREE.BufferGeometry, THREE.ShaderMaterial>;
  last?: { x: number; z: number; time: number }; heading: THREE.Vector3; speed: number; intensity: number; travelled: number; next: number; lastPuff: number; side: number; puff: number;
}

/**
 * Presentation only: a charging Goblin Ratrider (`combat.charging` or an earned `combat.charge`) that is actually
 * covering ground trails speed streaks and kicks up dust; both settle when it stops or the charge is spent.
 */
/** One trail's streaks and dust, for compiling their shaders before play: a first charge otherwise linked them mid-fight. */
export function chargeTrailWarmup(): THREE.Object3D[] { return [new THREE.Mesh(streakGeometry(1.6, .4), streakMaterial()), dustPoints()]; }

export class ChargeTrails {
  readonly group = new THREE.Group();
  private trails = new Map<string, Trail>();
  constructor(scene: THREE.Object3D, private heights: (id: string) => number = () => 1.6) {
    this.group.name = 'charge-trails'; scene.add(this.group);
  }
  /** `units` are the displayed snapshot's units; `groups` their interpolated, culled unit groups. Runs on the buffered game clock. */
  update(gameTime: number, units: readonly ChargeRunner[], groups: ReadonlyMap<string, THREE.Object3D>, ppm = 40) {
    const present = new Set<string>();
    for (const unit of units) {
      const group = groups.get(unit.id);
      const charging = !!(unit.combat.charging || unit.combat.charge) && !unit.health.dead;
      if (!group || (!charging && !this.trails.has(unit.id))) continue;
      present.add(unit.id);
      let trail = this.trails.get(unit.id);
      if (!trail) {
        trail = { streaks: new THREE.Mesh(streakGeometry(this.heights(unit.id), unit.radius), streakMaterial()), dust: dustPoints(),
          heading: new THREE.Vector3(0, 0, 1), speed: 0, intensity: 0, travelled: 0, next: 0, lastPuff: -1e6, side: 1, puff: 0 };
        trail.streaks.name = 'charge-streaks'; trail.streaks.frustumCulled = false;
        this.group.add(trail.streaks, trail.dust); this.trails.set(unit.id, trail);
      }
      this.advance(trail, unit, group, charging, gameTime, ppm);
    }
    for (const [id, trail] of this.trails) {
      if (present.has(id)) continue;
      // Gone from the snapshot (killed, fled, rewound): what is left settles on its own clock.
      trail.intensity = 0; trail.streaks.visible = false; trail.dust.material.uniforms.uTime.value = gameTime;
      if (gameTime - trail.lastPuff > PUFF_LIFE || gameTime < trail.lastPuff) this.drop(id);
    }
  }
  private advance(trail: Trail, unit: ChargeRunner, group: THREE.Object3D, charging: boolean, gameTime: number, ppm: number) {
    const { x, z } = group.position, last = trail.last, dt = last ? gameTime - last.time : 0;
    if (last && dt > 0) {
      const dx = x - last.x, dz = z - last.z, step = Math.hypot(dx, dz);
      // A jump (a teleport, a rewound world) is not a run.
      if (step / dt > 30 || dt > .5) trail.speed = 0;
      else {
        trail.speed += (step / dt - trail.speed) * Math.min(1, dt * 12);
        if (step > 1e-4) trail.heading.set(dx / step, 0, dz / step);
        trail.travelled += step;
      }
      trail.intensity += ((charging && trail.speed > CHARGE_TRAIL_SPEED ? 1 : 0) - trail.intensity) * Math.min(1, dt * 7);
    }
    if (dt < 0) trail.lastPuff = -1e6;
    trail.last = { x, z, time: gameTime };
    const shown = group.visible && trail.intensity > .02;
    trail.streaks.visible = shown;
    trail.streaks.position.copy(group.position); trail.streaks.rotation.y = Math.atan2(trail.heading.x, trail.heading.z);
    trail.streaks.scale.set(1, 1, Math.min(2.8, .4 + trail.speed * .3));
    trail.streaks.material.uniforms.uTime.value = gameTime; trail.streaks.material.uniforms.uOpacity.value = trail.intensity;
    const dust = trail.dust, uniforms = dust.material.uniforms;
    uniforms.uTime.value = gameTime; uniforms.uPpm.value = ppm; dust.visible = group.visible;
    if (trail.intensity > .3 && trail.travelled >= trail.next) {
      trail.next = trail.travelled + PUFF_SPACING;
      const position = dust.geometry.getAttribute('position') as THREE.BufferAttribute, birth = dust.geometry.getAttribute('aBirth') as THREE.BufferAttribute;
      const index = trail.puff = (trail.puff + 1) % PUFFS; trail.side = -trail.side;
      const back = unit.radius * .7, across = unit.radius * .45 * trail.side;
      position.setXYZ(index, x - trail.heading.x * back - trail.heading.z * across, group.position.y + .05, z - trail.heading.z * back + trail.heading.x * across);
      birth.setX(index, gameTime); position.needsUpdate = birth.needsUpdate = true; trail.lastPuff = gameTime;
    }
    if (!charging && trail.intensity <= .02 && gameTime - trail.lastPuff > PUFF_LIFE) this.drop(unit.id);
  }
  private drop(id: string) {
    const trail = this.trails.get(id); if (!trail) return;
    for (const object of [trail.streaks, trail.dust]) { object.removeFromParent(); object.geometry.dispose(); object.material.dispose(); }
    this.trails.delete(id);
  }
  /** How many runners have a live trail (for tests and frame statistics). */
  get size() { return this.trails.size; }
  clear() { for (const id of [...this.trails.keys()]) this.drop(id); }
  dispose() { this.clear(); this.group.removeFromParent(); }
}
